import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { Track, Feedback, Artist } from '../types/artist';
import { trackApi } from '../services/trackApi';
import { artistApi } from '../services/artistApi';
import { useAuth } from '../contexts/AuthContext';
import { ArtistOverview } from './shared/ArtistOverview';
import { TrackCard } from './shared/TrackCard';
import { TransportControl } from './shared/TransportControl';
import { Feedback as FeedbackComponent } from './shared/Feedback';
import './MyReviews.css';

interface PagedResult {
  items: Track[];
  page: number;
  pageSize: number;
  totalCount: number;
  totalPages: number;
  hasPreviousPage: boolean;
  hasNextPage: boolean;
}

const MyReviews: React.FC = () => {
  const { user } = useAuth();
  const location = useLocation();
  const [tracks, setTracks] = useState<Track[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedTrack, setSelectedTrack] = useState<Track | null>(null);
  const [currentTrackIndex, setCurrentTrackIndex] = useState<number>(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [volume, setVolume] = useState(1);
  const [isMuted, setIsMuted] = useState(false);
  const [previousVolume, setPreviousVolume] = useState(1);
  const [pageInfo, setPageInfo] = useState<PagedResult | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [showArtistModal, setShowArtistModal] = useState(false);
  const [fullArtist, setFullArtist] = useState<Artist | null>(null);
  const [shouldAutoPlay, setShouldAutoPlay] = useState(false);

  const audioRef = useRef<HTMLAudioElement | null>(null);
  
  // Extract publicId from URL path (e.g., /my-reviews/abc-123)
  const publicIdFromUrl = location.pathname.startsWith('/my-reviews/') 
    ? location.pathname.split('/my-reviews/')[1] 
    : null;

  // Load reviewed tracks
  const loadReviewedTracks = useCallback(async (page: number = 1) => {
    try {
      setLoading(true);
      setError(null);
      const result = await trackApi.getReviewedTracks(page, 20);
      
      setPageInfo(result);
      setTracks(result.items);
      
      // If we have tracks and no track is selected, select the first one
      // Use functional updates to check state without creating nested state updates
      let shouldSelectFirstTrack = false;
      
      setSelectedTrack(prev => {
        if (result.items.length > 0 && !prev) {
          shouldSelectFirstTrack = true;
          return result.items[0];
        }
        return prev;
      });
      
      // Update track index separately to avoid nested state updates
      if (shouldSelectFirstTrack) {
        setCurrentTrackIndex(0);
      }
    } catch (err) {
      console.error('Error loading reviewed tracks:', err);
      setError('Failed to load reviewed tracks. Please try again.');
    } finally {
      setLoading(false);
    }
  }, []); // No dependencies needed

  useEffect(() => {
    loadReviewedTracks(currentPage);
  }, [currentPage, loadReviewedTracks]);

  // Auto-select track based on publicId in URL
  useEffect(() => {
    if (publicIdFromUrl && tracks.length > 0) {
      const trackIndex = tracks.findIndex(
        track => track.publicId?.toLowerCase() === publicIdFromUrl.toLowerCase()
      );
      
      if (trackIndex !== -1) {
        const track = tracks[trackIndex];
        console.log('Auto-selecting track from URL:', track.title);
        setSelectedTrack(track);
        setCurrentTrackIndex(trackIndex);
      } else {
        console.log('Track with publicId not found on this page:', publicIdFromUrl);
      }
    }
  }, [publicIdFromUrl, tracks]);

  // Get user's feedback for the selected track
  const getUserFeedback = (track: Track): Feedback | undefined => {
    // Find the feedback entry created by the current user
    if (!track.feedback || track.feedback.length === 0 || !user?.id) return undefined;
    return track.feedback.find(fb => fb.createdByUserId === user.id);
  };

  // Handle track selection
  const handleTrackSelect = (track: Track, index: number, autoPlay: boolean = false) => {
    setSelectedTrack(track);
    setCurrentTrackIndex(index);
    setIsPlaying(false);
    setCurrentTime(0);
    setShouldAutoPlay(autoPlay);
  };

  // Play next track in the playlist
  const playNextTrack = useCallback(() => {
    if (currentTrackIndex < tracks.length - 1) {
      const nextIndex = currentTrackIndex + 1;
      const nextTrack = tracks[nextIndex];
      setSelectedTrack(nextTrack);
      setCurrentTrackIndex(nextIndex);
      setCurrentTime(0);
      setIsPlaying(true);
    } else {
      // End of playlist
      setIsPlaying(false);
    }
  }, [currentTrackIndex, tracks]);

  // Setup audio element and event listeners
  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;

    const handleTimeUpdate = () => setCurrentTime(audio.currentTime);
    const handleDurationChange = () => setDuration(audio.duration);
    const handleEnded = () => {
      console.log('Track ended, playing next...');
      playNextTrack();
    };
    const handleError = (e: Event) => {
      console.error('Audio error:', e);
      setError('Failed to load audio. Please try again.');
      setIsPlaying(false);
    };

    audio.addEventListener('timeupdate', handleTimeUpdate);
    audio.addEventListener('durationchange', handleDurationChange);
    audio.addEventListener('ended', handleEnded);
    audio.addEventListener('error', handleError);

    return () => {
      audio.removeEventListener('timeupdate', handleTimeUpdate);
      audio.removeEventListener('durationchange', handleDurationChange);
      audio.removeEventListener('ended', handleEnded);
      audio.removeEventListener('error', handleError);
    };
  }, [playNextTrack]);

  // Load audio when track changes
  useEffect(() => {
    if (selectedTrack && selectedTrack.id && audioRef.current) {
      const streamUrl = trackApi.getAudioStreamUrl(selectedTrack.id);
      audioRef.current.src = streamUrl;
      audioRef.current.load();
      
      // Handle auto-play after track selection
      if (shouldAutoPlay) {
        // Wait for the audio to be loaded before playing
        const handleCanPlay = () => {
          audioRef.current?.play().catch(err => {
            console.error('Error playing audio:', err);
            setIsPlaying(false);
          });
          setIsPlaying(true);
          setShouldAutoPlay(false);
          audioRef.current?.removeEventListener('canplay', handleCanPlay);
        };
        
        audioRef.current.addEventListener('canplay', handleCanPlay);
      } else if (isPlaying) {
        // If already playing, continue playing the new track
        audioRef.current.play().catch(err => {
          console.error('Error playing audio:', err);
          setIsPlaying(false);
        });
      }
    }
  }, [selectedTrack, isPlaying, shouldAutoPlay]);

  // Handle play/pause
  const handlePlayPause = () => {
    if (!audioRef.current || !selectedTrack) return;

    if (isPlaying) {
      audioRef.current.pause();
      setIsPlaying(false);
    } else {
      audioRef.current.play().catch(err => {
        console.error('Error playing audio:', err);
      });
      setIsPlaying(true);
    }
  };

  // Handle seek
  const handleSeek = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newTime = parseFloat(e.target.value);
    setCurrentTime(newTime);
    if (audioRef.current) {
      audioRef.current.currentTime = newTime;
    }
  };

  // Handle volume change
  const handleVolumeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newVolume = parseFloat(e.target.value);
    setVolume(newVolume);
    if (audioRef.current) {
      audioRef.current.volume = newVolume;
    }
    if (newVolume > 0 && isMuted) {
      setIsMuted(false);
    }
  };

  // Handle mute toggle
  const handleMuteToggle = () => {
    if (!audioRef.current) return;

    if (isMuted) {
      setVolume(previousVolume);
      audioRef.current.volume = previousVolume;
      setIsMuted(false);
    } else {
      setPreviousVolume(volume);
      setVolume(0);
      audioRef.current.volume = 0;
      setIsMuted(true);
    }
  };

  // Handle artist modal
  const handleArtistModalOpen = async (artistId: number | undefined) => {
    if (!artistId) return;
    
    setShowArtistModal(true);
    
    try {
      const artist = await artistApi.getArtistById(artistId);
      setFullArtist(artist);
    } catch (error) {
      console.error('Failed to fetch artist details:', error);
      // Keep the modal open but show basic info from track
      setFullArtist(selectedTrack?.artist || null);
    }
  };

  const handleArtistModalClose = () => {
    setShowArtistModal(false);
    setFullArtist(null);
  };

  // Handle pagination
  const handlePreviousPage = () => {
    if (pageInfo?.hasPreviousPage) {
      setCurrentPage(prev => prev - 1);
    }
  };

  const handleNextPage = () => {
    if (pageInfo?.hasNextPage) {
      setCurrentPage(prev => prev + 1);
    }
  };

  if (loading && tracks.length === 0) {
    return (
      <div className="my-reviews">
        <div className="loading">Loading your reviews...</div>
      </div>
    );
  }

  if (error && tracks.length === 0) {
    return (
      <div className="my-reviews">
        <div className="error-message">
          <p>{error}</p>
          <button onClick={() => loadReviewedTracks(currentPage)} className="retry-button">
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="my-reviews">
      {/* Hidden audio element */}
      <audio ref={audioRef} />

      {/* Main Content Layout */}
      <div className="main-content-layout">
        {/* Left Column - Track List */}
        <div className="left-column">
          <div className="track-list">
            <h3>Reviewed Tracks ({pageInfo?.totalCount || 0})</h3>
            {tracks.length === 0 ? (
              <div className="no-tracks">
                <p>You haven't reviewed any tracks yet.</p>
                <p>Visit the "Give Feedback" tab to start reviewing!</p>
              </div>
            ) : (
              <>
                <div className="tracks-list">
                  {tracks.map((track, index) => (
                    <TrackCard
                      key={track.id}
                      track={track}
                      isSelected={selectedTrack?.id === track.id}
                      isPlaying={isPlaying}
                      onClick={(track) => handleTrackSelect(track, index)}
                      onPlayPause={handlePlayPause}
                      showArtist={true}
                      showUploadDate={false}
                      showReviewDate={true}
                      showFeedbackCount={false}
                      showFeedbackSummary={false}
                      showShareButton={false}
                      showDeleteButton={false}
                      showReviewStatus={false}
                      reviewDate={getUserFeedback(track)?.createdAt
                        ? new Date(getUserFeedback(track)!.createdAt).toLocaleDateString()
                        : 'Unknown'}
                      trackIndex={index}
                      customPlayClick={(track, trackIdx, autoPlay) => 
                        handleTrackSelect(track, trackIdx || index, autoPlay)
                      }
                    />
                  ))}
                </div>

                {/* Pagination */}
                {pageInfo && pageInfo.totalPages > 1 && (
                  <div className="pagination">
                    <button 
                      onClick={handlePreviousPage} 
                      disabled={!pageInfo.hasPreviousPage}
                      className="pagination-button"
                    >
                      ← Previous
                    </button>
                    <span className="page-info">
                      Page {pageInfo.page} of {pageInfo.totalPages}
                    </span>
                    <button 
                      onClick={handleNextPage} 
                      disabled={!pageInfo.hasNextPage}
                      className="pagination-button"
                    >
                      Next →
                    </button>
                  </div>
                )}
              </>
            )}
          </div>
        </div>

        {/* Right Column - Audio Player and Review */}
        <div className="right-column">
          {selectedTrack ? (
            <>
              {/* Audio Player */}
              {(selectedTrack.audioUrl || selectedTrack.id) && (
                <TransportControl
                  track={selectedTrack}
                  isPlaying={isPlaying}
                  currentTime={currentTime}
                  duration={duration}
                  volume={volume}
                  isMuted={isMuted}
                  onPlayPause={handlePlayPause}
                  onSeek={handleSeek}
                  onVolumeChange={handleVolumeChange}
                  onMuteToggle={handleMuteToggle}
                  showTrackCounter={true}
                  trackCounter={`Track ${currentTrackIndex + 1} of ${tracks.length}`}
                  onArtistClick={handleArtistModalOpen}
                  audioRef={audioRef}
                />
              )}

              {/* Producer Notes */}
              {(selectedTrack.producerNotes?.length ?? 0) > 0 && (
                <div className="producer-notes-section">
                  <h3>Producer Notes</h3>
                  <p>{selectedTrack.producerNotes}</p>
                </div>
              )}

              {/* User's Review */}
              {getUserFeedback(selectedTrack) && (
                <div className="review-section">
                  <h3>Your Review</h3>
                  <FeedbackComponent
                    feedback={getUserFeedback(selectedTrack)!}
                    variant="card"
                    showAuthor={false}
                    showThreadButton={true}
                  />
                </div>
              )}
            </>
          ) : (
            <div className="no-track-selected">
              <h3>Select a track to replay and view your review</h3>
              <p>Click on any track from the list to start listening</p>
            </div>
          )}
        </div>
      </div>

      {/* Artist Modal */}
      {showArtistModal && (fullArtist || selectedTrack?.artist) && (
        <ArtistOverview 
          artist={fullArtist || selectedTrack!.artist!}
          showAsModal={true}
          onClose={handleArtistModalClose}
        />
      )}
    </div>
  );
};

export default MyReviews;
